import type { HTMLAttributes } from "react";
import type {
  TNewsletterSignupProps,
} from "@/ui/components/sections/marketing/NewsletterSignUp";
import type { TIconKey } from "@/ui/components/primitives/Icon";
import { cn, type DefinedDefaults, type TWithDataComponent } from "@/ui/lib";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const FOOTER_MAIN_CONTAINER_CLASS_NAME = cn(
  "relative flex w-fit flex-col gap-4 px-3 pb-4 pt-8",
  "md:gap-6 md:px-0 md:pb-8 md:pt-6",
  "xl:gap-12 xl:pb-12 xl:pt-16",
);

export const FOOTER_PRIMARY_NAVIGATION_LIST_CLASS_NAME = cn(
  "grid grid-cols-2",
  "md:w-[598px] md:grid-cols-4 md:gap-x-6",
  "xl:w-[1032px] xl:gap-x-12",
);

export const FOOTER_HANGING_CAT_CLASS_NAME =
  "pointer-events-none absolute right-0 top-0 z-0 hidden w-24 md:block";

export const FOOTER_SECONDARY_TEXT_CLASS_NAME =
  "text-sys-base leading-sys-1-3 text-sys-neutral-600 xl:text-sys-lg";

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TFooterNavigationLink = {
  id: string;
  label: string;
  href: string;
  icon?: TIconKey;
  ariaLabel?: string;
};

export type TFooterNavigationSection = {
  id: string;
  title: string;
  links: TFooterNavigationLink[];
};

export type TFooterProps = Omit<HTMLAttributes<HTMLElement>, "children"> &
  TWithDataComponent & {
    primaryNavigation: TFooterNavigationSection[];
    secondaryNavigation: TFooterNavigationLink[];
    socialNavigation: TFooterNavigationLink[];
    newsletter?: TNewsletterSignupProps;
    isSignedIn?: boolean;
  };

type TFooterDefaultProps = DefinedDefaults<
  TFooterProps,
  "data-component"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const FOOTER_DEFAULT_NEWSLETTER_PROPS: TNewsletterSignupProps = {
  title: "Join KatKin",
  body: "Sign up for hardcore news, cat education and exclusive offers.",
  button: {
    children: "Sign up",
  },
  input: {
    name: "newsletter_email",
    placeholder: "Email address",
    type: "email",
  },
};

export const FOOTER_DEFAULTS = {
  "data-component": "Footer",
} satisfies TFooterDefaultProps;
